import { __awaiter, __decorate, __param, __rest } from "tslib";
import { observable, volatile } from '@microsoft/fast-element';
import { DI, optional } from '@microsoft/fast-foundation';
import { combineLatest, from, timer } from 'rxjs';
import { switchMap, takeWhile } from 'rxjs/operators';
import { Auth } from '../auth';
import { Connect } from '../connect';
import { extractFieldDefinitions, toFieldMetadata } from '../metadata/metadata.utils';
import { logger } from '../utils';
import { criteriaFiltersToFields } from './criteriaFiltersToFields';
import { dataServerResultFilter } from './dataServerResultFilter';
import { DatasourceConfig, DatasourceDefaults, ResourceType } from './datasource.types';
/**
 * The default configuration for the Datasource service.
 * @public
 */
export const defaultDatasourceConfig = {
    options: {
        criteria: '',
        isSnapshot: false,
        maxRows: DatasourceDefaults.MAX_ROWS_250,
        maxView: DatasourceDefaults.MAX_VIEW_1000,
        pollingInterval: DatasourceDefaults.REQ_REP_POLLING_INTERVAL_MS,
        movingView: false,
        orderBy: '',
        resourceName: '',
        reverse: false,
    },
    criteriaToFields: criteriaFiltersToFields,
    dataServerResultFilter: dataServerResultFilter,
};
/**
 * The default implementation for the Datasource interface.
 * @public
 */
let DefaultDatasource = class DefaultDatasource {
    constructor(connect, auth, config = defaultDatasourceConfig) {
        this.connect = connect;
        this.auth = auth;
        this.config = config;
        this.initialized = false;
        this.readOnly = true;
        this.originalFieldDef = [];
        this.requestFields = [];
        this.fieldMetadata = [];
        this.availableIndexes = [];
    }
    validResourceName(resourceName) {
        if (!resourceName)
            return false;
        if (!resourceName.trim().length)
            return false;
        // additional checks? regex? or maybe we can ask the server if it's a valid resource in the future
        return true;
    }
    init(options, fetchMeta = true) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.validResourceName(options === null || options === void 0 ? void 0 : options.resourceName)) {
                return false;
            }
            this.options = Object.assign(Object.assign({}, this.config.options), options);
            if (fetchMeta) {
                logger.debug(`Initializing ${this.options.resourceName} Datasource`);
                const meta = yield this.connect
                    .getMetadata(this.options.resourceName)
                    .catch((e) => logger.error(`Metadata Fetch failed for ${options.resourceName}:`, e));
                if (!meta) {
                    this.deinit();
                    return false;
                }
                this.resourceType = meta.TYPE;
                this.availableIndexes = (_a = meta.INDEXES) !== null && _a !== void 0 ? _a : [];
                this.originalFieldDef = extractFieldDefinitions(meta);
                this.fieldMetadata = toFieldMetadata(this.originalFieldDef);
                this.requestFields = meta.REQUEST_FIELD || [];
                logger.debug(`Datasource for ${this.options.resourceName} is ready`);
            }
            if (!this.options.isSnapshot) {
                this._stream = yield this.createStream();
            }
            this.initialized = true;
            return this.initialized;
        });
    }
    deinit() {
        this.initialized = false;
        this.options = undefined;
        this.resourceType = undefined;
        this.availableIndexes = [];
        this.originalFieldDef = [];
        this.fieldMetadata = [];
        this.requestFields = [];
    }
    get stream() {
        return this._stream;
    }
    get params() {
        const params = {
            CRITERIA_MATCH: this.options.criteria,
            FIELDS: this.options.fields,
            MAX_ROWS: this.options.maxRows,
            MAX_VIEW: this.options.maxView,
            MOVING_VIEW: this.options.movingView,
            POLLING_INTERVAL: this.options.pollingInterval,
            DISABLE_POLLING: this.options.disablePolling,
            REQUEST: this.options.request,
            REVERSE: this.options.reverse,
        };
        if (this.options.orderBy) {
            if (this.availableIndexes.findIndex((i) => i.NAME === this.options.orderBy) > -1) {
                params.ORDER_BY = this.options.orderBy;
            }
            else {
                const validIndexes = this.availableIndexes.map((index) => index.NAME);
                logger.warn('Specified [orderBy] index is not valid and will not be used. See https://learn.genesis.global/docs/database/data-types/index-entities/ - Available indexes:', validIndexes);
            }
        }
        logger.debug('Getting configured params:', params);
        return params;
    }
    snapshot() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            logger.debug(`Requesting snapshot for ${this.options.resourceName} (${this.resourceType}).`);
            switch (this.resourceType) {
                case ResourceType.DATASERVER:
                    return this.connect.snapshot(this.options.resourceName, this.params);
                case ResourceType.REQUEST_SERVER: {
                    const _b = this.params, { REQUEST, CRITERIA_MATCH } = _b, DETAILS = __rest(_b, ["REQUEST", "CRITERIA_MATCH"]);
                    const requestFields = Object.assign(Object.assign(Object.assign({}, Object.fromEntries(this.requestFields.map((rf) => [rf.NAME, '*']))), this.params.REQUEST), (((_a = this === null || this === void 0 ? void 0 : this.options) === null || _a === void 0 ? void 0 : _a.criteria) ? this.config.criteriaToFields(this.options.criteria) : {}));
                    const reqParams = {
                        DETAILS,
                        REQUEST: requestFields,
                    };
                    return this.connect.request(this.options.resourceName, reqParams);
                }
                default:
                    throw new Error(`Snapshot not supported for selected resource type: ${this.resourceType}`);
            }
        });
    }
    snapshotFiltered(rowId = 'ROW_REF') {
        return __awaiter(this, void 0, void 0, function* () {
            const result = yield this.snapshot();
            if (Array.isArray(result)) {
                return result;
            }
            else if (result.REPLY) {
                return result.REPLY;
            }
            else if (result.ROW) {
                return this.config.dataServerResultFilter(result, rowId).inserts;
            }
        });
    }
    createStream() {
        return __awaiter(this, void 0, void 0, function* () {
            if (![ResourceType.DATASERVER, ResourceType.REQUEST_SERVER].includes(this.resourceType)) {
                throw new Error(`Streaming not supported for selected resource type: ${this.resourceType}`);
            }
            if (this.resourceType === ResourceType.DATASERVER) {
                return this.createDataserverStream();
            }
            logger.debug(`Creating fake stream by polling ${this.options.resourceName} REQUEST_SERVER`);
            return this.createReqRepStream();
        });
    }
    createDataserverStream() {
        return __awaiter(this, void 0, void 0, function* () {
            const onMessage = (message) => { };
            const onError = (error) => logger.error(error);
            return this.connect.stream(this.options.resourceName, onMessage, onError, this.params);
        });
    }
    createReqRepStream() {
        return timer(0, this.params.DISABLE_POLLING ? undefined : this.params.POLLING_INTERVAL).pipe(switchMap(() => {
            // Combine the isConnected status with the isLoggedIn status
            return combineLatest([this.connect.isConnected$, this.auth.isLoggedIn$]).pipe(
            // Use takeWhile to continue polling until isConnected becomes false or isLoggedIn becomes false
            takeWhile(() => this.connect.isConnected && this.auth.isLoggedIn), // Use switchMap to get the snapshot data
            switchMap(() => from(this.snapshot())));
        }));
    }
};
__decorate([
    observable
], DefaultDatasource.prototype, "initialized", void 0);
__decorate([
    observable
], DefaultDatasource.prototype, "readOnly", void 0);
__decorate([
    volatile
], DefaultDatasource.prototype, "stream", null);
DefaultDatasource = __decorate([
    __param(0, Connect),
    __param(1, Auth),
    __param(2, optional(DatasourceConfig))
], DefaultDatasource);
export { DefaultDatasource };
/**
 * The DI token for the Datasource interface.
 * @internal
 */
export const Datasource = DI.createInterface((x) => x.transient(DefaultDatasource));
/**
 * Get Datasource from the DI container.
 *
 * @remarks
 * A utility method for host applications that are not using decorators or the DI container.
 *
 * @example
 * ```ts
 * import { FilteredDataServerResult, getDatasource, SocketObservable } from '@genesislcap/foundation-comms';
 * ...
 * private ds = getDatasource();
 * private dsSub: SocketObservable<FilteredDataServerResult>;
 * ...
 * const dsInit = await this.ds.init({
 *   criteria: '',
 *   isSnapshot: false,
 *   resourceName: 'ALL_POSITIONS',
 *   reverse: false,
 *   maxRows: 250,
 *   maxView: 1000,
 * });
 * if (!dsInit) {
 *   logger.debug(`Failed to initialise 'ALL_POSITIONS' datasource`);
 *   return;
 * }
 * this.dsSub = this.ds.stream.subscribe((result) => { ... });
 * ```
 *
 * @public
 */
export function getDatasource() {
    return DI.getOrCreateDOMContainer().get(Datasource);
}
